import { z } from 'zod';
import { idempotency } from './spine';
export { scheduleOverrideInput, type ScheduleOverrideInput } from './spine';

const uuid = z.string().uuid();
/** The server validates IANA timezone support before saving; length alone is not validity. */
export const crewCalendarSettingsInput = idempotency
  .extend({
    timezone: z.string().trim().min(1).max(100),
    enabled: z.boolean(),
    travelBufferMinutes: z.number().int().min(0).max(240),
    defaultDurationMinutes: z.number().int().min(1).max(1440),
    expectedVersion: z.number().int().positive().optional(),
  })
  .strict();
export type CrewCalendarSettingsInput = z.infer<typeof crewCalendarSettingsInput>;
export const crewWorkingHoursEntry = z
  .object({
    weekday: z.number().int().min(0).max(6),
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(1).max(1440),
    active: z.boolean().default(true),
  })
  .strict()
  .refine((v) => v.startMinute < v.endMinute, {
    message: 'Working hours must end after they start',
    path: ['endMinute'],
  });
export const crewWorkingHoursInput = idempotency
  .extend({
    userId: uuid.nullable(),
    entries: z.array(crewWorkingHoursEntry).max(28),
    expectedVersion: z.number().int().positive(),
  })
  .strict();
export type CrewWorkingHoursInput = z.infer<typeof crewWorkingHoursInput>;
export const crewTimeOffCreateInput = idempotency
  .extend({
    userId: uuid,
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    reason: z.string().trim().max(500).optional(),
  })
  .strict()
  .refine((v) => v.startsAt < v.endsAt, {
    message: 'Time off must end after it starts',
    path: ['endsAt'],
  });
export type CrewTimeOffCreateInput = z.infer<typeof crewTimeOffCreateInput>;
export const crewTimeOffDeleteInput = idempotency.extend({ timeOffId: uuid }).strict();
export type CrewTimeOffDeleteInput = z.infer<typeof crewTimeOffDeleteInput>;
export const scheduleAvailabilityInput = z
  .object({
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    userIds: z.array(uuid).min(1).max(100).optional(),
    excludeVisitId: uuid.optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.startsAt < v.endsAt && v.endsAt.getTime() - v.startsAt.getTime() <= 31 * 86400000,
    { message: 'Choose a positive interval of at most 31 days', path: ['endsAt'] },
  );
export type ScheduleAvailabilityInput = z.infer<typeof scheduleAvailabilityInput>;
export const scheduleTimelineInput = z
  .object({
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    userIds: z.array(uuid).min(1).max(100).optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.startsAt < v.endsAt && v.endsAt.getTime() - v.startsAt.getTime() <= 8 * 86400000,
    {
      message: 'Choose a positive timeline interval of at most eight days',
      path: ['endsAt'],
    },
  );
export type ScheduleTimelineInput = z.infer<typeof scheduleTimelineInput>;
export type CrewAvailabilityState = 'available' | 'conflict' | 'unknown';
